import {ApplicationController} from "../../support/application-controller";
import queryString from "query-string";

export default class extends ApplicationController {
    static targets = [
        "product",
        "type",
        "startDate",
        "endDate",
        "price",
        "maximumConcurrentUsers",
        "invoiceStatus",
        "invoicePaymentDueDate",
        "invoiceNumber",
        "productList",
        "productListEmptyWarning",
        "addNewProductButton",
        "removeProductButton",
        "saveChangesButton",
        "resetChangesButton",
        "cancelSubscriptionButton",
        "selectNewProductId",
        "loadingIndicator",
        "dateInput",
        "renewalPeriod",
        "reportLimit",
        "currentPeriodUsage",
        "reportLimitDefaultButton",
    ];

    connect() {
        this.manageAddNewProductButtonVisibility();

        this._currentPlaceholderId = 0;
        this._removedProductsIds = [];
    }

    checkDates() {
        let areAllDatesValid = true;

        // Disable the save button if ANY date input has an invalid date.
        this.dateInputTargets.forEach((dateTarget) => {
            const date = new Date(dateTarget.value);
            if (isNaN(date)) {
                // Only valid dates are converted to numbers.
                this.setInputValidity(dateTarget, false);
                this.setButtonState(this.saveChangesButtonTarget, false);
                areAllDatesValid = false;
            } else {
                this.setInputValidity(dateTarget, true);
            }
        });

        const startDate = new Date(this.startDateTarget.value);
        const endDate = new Date(this.endDateTarget.value);

        if (startDate > endDate) {
            this.setInputValidity(this.startDateTarget, false);
            this.setInputValidity(this.endDateTarget, false);
            this.setButtonState(this.saveChangesButtonTarget, false);
        }

        // If all is all okay then enable the save button!
        if (areAllDatesValid && startDate <= endDate) {
            this.setButtonState(this.saveChangesButtonTarget, true);
        }
    }

    setInputValidity(target, isValid) {
        if (isValid) {
            target.classList.remove("invalid");
        } else {
            target.classList.add("invalid");
        }
    }

    setButtonState(target, isEnabled) {
        target.disabled = !isEnabled;

        if (isEnabled) {
            target.classList.remove("button-raised--disabled");
        } else {
            target.classList.add("button-raised--disabled");
        }
    }

    manageAddNewProductButtonVisibility() {
        const currentlyAddedProducts = Array.from(
            this.productListTarget.children
        ).map(
            (productItemTarget) =>
                productItemTarget.dataset.subscriptionProductProductId
        );

        this.addNewProductButtonTarget.classList.toggle(
            "hidden",
            currentlyAddedProducts.length === this.allSupportedProducts.length
        );

        this.productListEmptyWarningTarget.classList.toggle(
            "hidden",
            currentlyAddedProducts.length !== 0
        );
    }

    populateAvailableProductIds() {
        const selectNewProductIdTarget = this.selectNewProductIdTarget;
        const currentlyAddedProductsListTarget = this.productListTarget;
        const currentlyAddedProducts = Array.from(
            currentlyAddedProductsListTarget.children
        ).map(
            (productItemTarget) =>
                productItemTarget.dataset.subscriptionProductProductId
        );

        selectNewProductIdTarget.innerHTML = "";
        const accessGroup = document.createElement("optgroup");
        accessGroup.label = "Application access";
        const dataPackageGroup = document.createElement("optgroup");
        dataPackageGroup.label = "Data package";
        this.allSupportedProducts
            .filter(
                (supportedProduct) =>
                    !currentlyAddedProducts.includes(supportedProduct.id)
            )
            .forEach((supportedProduct) => {
                const option = document.createElement("option");
                option.value = supportedProduct.id;
                option.innerHTML = supportedProduct.name;
                switch (supportedProduct.type) {
                    case "application access":
                        accessGroup.appendChild(option);
                        break;
                    case "data package":
                        dataPackageGroup.appendChild(option);
                        break;
                }
            });
        selectNewProductIdTarget.appendChild(accessGroup);
        selectNewProductIdTarget.appendChild(dataPackageGroup);
    }

    onAddProductToSubscription() {
        const productId = this.selectNewProductIdTarget.value;
        this.addProductToSubscription(productId).then(() => {
            // after app access product is added, add its default data package if not already added
            const isProductTypeApplicationAccess =
                this.allSupportedProducts.find((product) => product.id === productId)
                    .type === "application access";

            if (isProductTypeApplicationAccess) {
                const currentlyAddedProductsIds = Array.from(
                    this.productListTarget.children
                ).map(
                    (productItemTarget) =>
                        productItemTarget.dataset.subscriptionProductProductId
                );

                const defaultDataPackageProductIdForThisProduct =
                    this.defaultDataPackagesForApplications[productId];
                const isDefaultDataPackageAlreadyAdded = currentlyAddedProductsIds.some(
                    (currentlyAddedProductId) =>
                        currentlyAddedProductId ===
                        defaultDataPackageProductIdForThisProduct
                );

                if (!isDefaultDataPackageAlreadyAdded) {
                    this.addProductToSubscription(
                        defaultDataPackageProductIdForThisProduct
                    );
                }
            }
        });
    }

    addProductToSubscription(productId) {
        const data = queryString.stringify({
            placeholder_id: this.nextPlaceholderId,
            product_id: productId,
        });

        return this.railsGet({
            data,
            url: this.retrieveNewProductTemplateUrl,
            dataType: "json",
        }).then(({html}) => {
            // retrieved HTML needs to be parsed as a node so it can be prepended to the list
            // parsing always produces a document that then has to be navigated to extract the contents
            const htmlDocumentWithNewListItemElement =
                new DOMParser().parseFromString(html, "text/html");
            const newListItemElement =
                htmlDocumentWithNewListItemElement.firstElementChild.children[1]
                    .firstElementChild;
            this.productListTarget.prepend(newListItemElement);

            this.manageAddNewProductButtonVisibility();
        });
    }

    removeProductFromSubscription(e) {
        const subscriptionProductId = e.target.dataset.subscriptionProductId;
        const productTargetToRemove = this.productTargets.find(
            (productTarget) =>
                productTarget.dataset.subscriptionProductId === subscriptionProductId
        );

        for (let productElement of this.productListTarget.children) {
            if (productElement === productTargetToRemove) {
                this.productListTarget.removeChild(productElement);
                if (subscriptionProductId >= 0) {
                    this.removedProductsIds.push(subscriptionProductId);
                }
                break;
            }
        }

        this.manageAddNewProductButtonVisibility();
    }

    onProductPriceUpdated(e) {
        const subscriptionProductId = e.target.dataset.subscriptionProductId;
        const subscriptionProductTarget = this.productTargets.find(
            (productTarget) =>
                productTarget.dataset.subscriptionProductId === subscriptionProductId
        );

        subscriptionProductTarget.dataset.subscriptionProductPrice = e.target.value;
        subscriptionProductTarget.dataset.subscriptionProductModified = "true";
    }

    onProductFeaturesUpdated(e) {
        const subscriptionProductId = e.target.dataset.subscriptionProductId;
        const featureId = e.target.dataset.featureId;
        const subscriptionProductTarget = this.productTargets.find(
            (productTarget) =>
                productTarget.dataset.subscriptionProductId === subscriptionProductId
        );
        let features = subscriptionProductTarget.dataset.subscriptionProductFeatures
            ? JSON.parse(
                subscriptionProductTarget.dataset.subscriptionProductFeatures
            )
            : [];

        if (e.target.checked) {
            if (!features.includes(featureId)) features.push(featureId);
        } else {
            features = features.filter((id) => id !== featureId);
        }

        subscriptionProductTarget.dataset.subscriptionProductFeatures = JSON.stringify(features);
        subscriptionProductTarget.dataset.subscriptionProductModified = "true";

        const OPERATING_PERFORMANCE_PAGE =
            "ACCESS__TRACTIQ_SELFSTORAGE__ENABLE_OPERATING_PERFORMANCE_PAGE";

        const EXPORT_FEATURES = [
            "ACCESS__TRACTIQ_SELFSTORAGE__ENABLE_FINANCIAL_DATA_EXPORT",
        ];

        const operatingPerformanceCheck = this.element.querySelector(
            `[data-feature-id="${OPERATING_PERFORMANCE_PAGE}"][data-subscription-product-id="${subscriptionProductId}"]`
        );
        const exportCheckboxes = EXPORT_FEATURES
            .map((fid) =>
                this.element.querySelector(
                    `[data-feature-id="${fid}"][data-subscription-product-id="${subscriptionProductId}"]`
                )
            )
            .filter(Boolean);

        const exportLabels = exportCheckboxes.map((checkbox) => {
            const container = checkbox.closest(".subscription-editor__feature-item");
            return container ? container.querySelector("label") : null;
        });

        if (featureId === OPERATING_PERFORMANCE_PAGE) {
            exportCheckboxes.forEach((checkbox, index) => {
                const label = exportLabels[index];

                if (e.target.checked) {
                    checkbox.disabled = false;
                    checkbox.title = "";
                    if (label) label.classList.remove("subscription-editor__feature-label--disabled");
                } else {
                    checkbox.checked = false;
                    checkbox.disabled = true;
                    checkbox.title = "Enable 'Operating Performance Page' first";
                    if (label) label.classList.add("subscription-editor__feature-label--disabled");

                    features = features.filter((id) => id !== checkbox.dataset.featureId);
                }
            });

            subscriptionProductTarget.dataset.subscriptionProductFeatures =
                JSON.stringify(features);
        }

        // Prevent enabling export if operating performance is off
        if (
            EXPORT_FEATURES.includes(featureId) &&
            e.target.checked &&
            operatingPerformanceCheck &&
            !operatingPerformanceCheck.checked
        ) {
            e.preventDefault();
            e.target.checked = false;
            e.target.disabled = true;
            setTimeout(() => (e.target.disabled = false), 200);

            const container = e.target.closest(".subscription-editor__feature-item");
            const label = container ? container.querySelector("label") : null;

            if (label) label.classList.add("subscription-editor__feature-label--disabled");
            e.target.title = "Enable 'Operating Performance Page' first";
        }

    }

    cancelSubscription(e) {
        const onCancelledRedirectTo =
            e.target.dataset.onSubscriptionCancelledRedirectTo;

        this.loadingIndicatorTarget.classList.remove("hidden");
        const xmlHttp = new XMLHttpRequest();
        xmlHttp.onreadystatechange = () => {
            if (xmlHttp.readyState === 4) {
                if (xmlHttp.status === 200) {
                    window.location.href = onCancelledRedirectTo;
                } else {
                    console.warn(xmlHttp.status);
                    console.warn(xmlHttp.response);
                }
            }
        };
        xmlHttp.open("POST", this.submitCancelUrl);
        xmlHttp.setRequestHeader("Content-Type", "application/json");
        xmlHttp.send(JSON.stringify({}));
    }

    resetSubscriptionEditor() {
        window.location.reload();
    }

    reportLimitFocus() {
        const v = (this.reportLimitTarget.value || "").trim();
        if (/^Default \(\d+\)/.test(v)) {
            this.reportLimitTarget.value = "";
        }
        this._updateDefaultButtonState();
    }

    reportLimitInput() {
        this._updateDefaultButtonState();
    }

    reportLimitBlur() {
        if (this.reportLimitTarget.value === "") {
            this.reportLimitTarget.value = `Default (${this.defaultReportLimit})`;
        }
        this._updateDefaultButtonState();
    }

    setDefaultReportLimit() {
        this.reportLimitTarget.value = `Default (${this.defaultReportLimit})`;
        this._updateDefaultButtonState();
    }

    _updateDefaultButtonState() {
        if (!this.hasReportLimitDefaultButtonTarget) return;
        const v = (this.reportLimitTarget.value || "").trim();
        const isDefaultDisplay = v === "" || /^Default \(\d+\)/.test(v);
        this.reportLimitDefaultButtonTarget.disabled = isDefaultDisplay;
    }

    saveUpdatedSubscription() {
        const allProductsTargets = Array.from(this.productListTarget.children);

        const productsToAdd = allProductsTargets
            .filter(
                (productItemTarget) =>
                    productItemTarget.dataset.subscriptionProductId < 0
            )
            .reverse() // to preserve ordering upon page reload, new items are prepended
            .map((productItemTarget) =>
                this.productTargetToObject(productItemTarget)
            );
        const productsToUpdate = allProductsTargets
            .filter(
                (productItemTarget) =>
                    productItemTarget.dataset.subscriptionProductId >= 0 &&
                    productItemTarget.dataset.subscriptionProductModified === "true"
            )
            .map((productItemTarget) =>
                this.productTargetToObject(productItemTarget)
            );
        const productIdsToRemove = this.removedProductsIds;

        const {reportLimit, reportLimitIsDefault} =
            this._computeReportLimitParams();

        const data = {
            type: this.typeTarget.value,
            startDate: this.startDateTarget.value,
            endDate: this.endDateTarget.value,
            price: Number(this.priceTarget.value),
            maximumConcurrentUsers: this.maximumConcurrentUsersTarget.value
                ? Number(this.maximumConcurrentUsersTarget.value)
                : undefined,
            invoiceStatus: this.invoiceStatusTarget.value,
            invoicePaymentDueDate: this.invoicePaymentDueDateTarget.value,
            invoiceNumber: this.invoiceNumberTarget.value,
            renewalPeriod: this.renewalPeriodTarget.value,
            reportLimit: reportLimit,
            reportLimitIsDefault: reportLimitIsDefault,
            currentPeriodUsage: this.currentPeriodUsageTarget.value,
            productsToAdd,
            productsToUpdate,
            productIdsToRemove,
        };

        this.loadingIndicatorTarget.classList.remove("hidden");
        const xmlHttp = new XMLHttpRequest();
        xmlHttp.onreadystatechange = () => {
            if (xmlHttp.readyState === 4) {
                if (xmlHttp.status === 200) {
                    location.reload();
                } else {
                    console.warn(xmlHttp.status);
                    console.warn(xmlHttp.response);
                }
            }
        };
        xmlHttp.open("POST", this.submitUpdateUrl);
        xmlHttp.setRequestHeader("Content-Type", "application/json");
        xmlHttp.send(JSON.stringify(data));
    }

    _computeReportLimitParams() {
        const raw = (this.reportLimitTarget.value || "").trim();

        // If empty or showing Default(...), treat as default
        if (raw === "" || /^Default \(\d+\)/.test(raw)) {
            return {reportLimit: null, reportLimitIsDefault: true};
        }

        // Otherwise pass raw value as string; backend will cast
        return {reportLimit: raw, reportLimitIsDefault: false};
    }

    productTargetToObject(productItemTarget) {
        const value = productItemTarget.dataset.subscriptionProductId;
        return {
            id: value < 0 ? null : value, // eliminate placeholders prior to backend dispatch
            subscriptionId: productItemTarget.dataset.subscriptionId,
            productId: productItemTarget.dataset.subscriptionProductProductId,
            configuration: productItemTarget.dataset.subscriptionProductFeatures
                ? {
                    features: JSON.parse(
                        productItemTarget.dataset.subscriptionProductFeatures || "[]"
                    ),
                }
                : null,
            price: Number(productItemTarget.dataset.subscriptionProductPrice),
        };
    }

    clearPeriodUsage() {
        if (this.hasLoadingIndicatorTarget) {
            this.loadingIndicatorTarget.classList.remove("hidden");
        }

        const xhr = new XMLHttpRequest();
        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
                if (xhr.status === 200) {
                    window.location.reload();
                } else {
                    console.warn(xhr.status);
                    console.warn(xhr.response);
                    if (this.hasLoadingIndicatorTarget) {
                        this.loadingIndicatorTarget.classList.add("hidden");
                    }
                }
            }
        };
        xhr.open("POST", this.resetReportUsageUrl);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.send(JSON.stringify({}));
    }

    get originalSubscription() {
        return JSON.parse(this.data.get("subscription"));
    }

    get allSupportedProducts() {
        return JSON.parse(this.data.get("supportedProducts")) || [];
    }

    get defaultDataPackagesForApplications() {
        return (
            JSON.parse(this.data.get("defaultDataPackagesForApplications")) || {}
        );
    }

    get submitUpdateUrl() {
        return this.data.get("submitUpdateUrl");
    }

    get submitCancelUrl() {
        return this.data.get("submitCancelUrl");
    }

    get retrieveNewProductTemplateUrl() {
        return this.data.get("retrieveNewProductTemplateUrl");
    }

    // Expose reset endpoint for clearing usage and starting a new period
    get resetReportUsageUrl() {
        return this.data.get("resetReportUsageUrl");
    }

    get removedProductsIds() {
        return this._removedProductsIds;
    }

    set removedProductsIds(removedProductsIds) {
        this._removedProductsIds = removedProductsIds;
    }

    get nextPlaceholderId() {
        this._currentPlaceholderId -= 1;
        return this._currentPlaceholderId;
    }

    get defaultReportLimit() {
        return this.data.get("defaultReportLimit");
    }
}
